
import { useState, useEffect } from 'react';
import {
  Code,
  Copy,
  CheckCircle,
  Globe,
  FileText,
  Settings,
  ArrowRight,
  Zap,
  Shield
} from 'lucide-react';
import ModernSettingHeader from '../../../components/settings/ModernSettingHeader';
import { getClientIntegrations } from '../../../actions/data-fetch-settings';
import { fetchLeadColumns } from '../../../actions/lead-manager';
import { Column } from '../../../types/columns';
import Loader from '../../../components/common/Loader/Loader';


const WebsiteIntegrationPage = () => {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [selectedMethod, setSelectedMethod] = useState<'jquery' | 'axios' | 'fetch' | 'html'>('jquery');
  const [loading, setLoading] = useState(true);
  const [accessCode, setAccessCode] = useState<string>('');
  const [columns, setColumns] = useState<Column[]>([]);

  // Updated API endpoint
  const apiEndpoint = "https://leadapi.zeroharm.in/api/leads/web";

  // Fetch access code and columns on component mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch client integrations and find integration_type 9
      const integrationsResponse = await getClientIntegrations();
      console.log('Integrations response:', integrationsResponse);

      // Check for both 'success' and 'status' fields
      const isSuccess = integrationsResponse.success === true || integrationsResponse.status === 'success';

      if (isSuccess && integrationsResponse.data?.integrations) {
        const webIntegration = integrationsResponse.data.integrations.find(
          (integration) => (integration.type === 9 || integration.integration_type === 9)
        );
        console.log('Web integration found:', webIntegration);

        if (webIntegration) {
          // Handle both code and integration_code field names
          const code = webIntegration.code || webIntegration.integration_code || '';
          console.log('Access code:', code);
          setAccessCode(code);
        }
      }

      // Fetch active columns with options
      const columnsResponse = await fetchLeadColumns();
      if (columnsResponse.success && columnsResponse.columns) {
        // Filter for active columns only
        const activeColumns = columnsResponse.columns.filter((col: Column) => col.col_active);
        setColumns(activeColumns);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (code: string, type: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(type);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // Generate HTML form fields dynamically based on columns
  const generateFormFields = () => {
    return columns
      .sort((a, b) => a.col_order - b.col_order)
      .map((column) => {
        const fieldId = `customer-${column.col_name}`;
        const isRequired = column.col_required;
        const requiredAttr = isRequired ? ' required' : '';

        if (column.col_type === 'select' && column.options.length > 0) {
          // Generate select dropdown
          const activeOptions = column.options.filter(opt => opt.opt_active)
            .sort((a, b) => a.opt_order - b.opt_order);

          return `            <div class="form-group">
                <label for="${fieldId}">${column.col_label}${isRequired ? ' *' : ''}</label>
                <select id="${fieldId}" name="${column.col_name}"${requiredAttr}>
                    <option value="">Please Select</option>
${activeOptions.map(opt => `                    <option value="${opt.uuid}">${opt.opt_name}</option>`).join('\n')}
                </select>
            </div>`;
        } else if (column.col_type === 'textarea') {
          return `            <div class="form-group">
                <label for="${fieldId}">${column.col_label}${isRequired ? ' *' : ''}</label>
                <textarea id="${fieldId}" name="${column.col_name}"${requiredAttr} rows="4"></textarea>
            </div>`;
        } else if (column.col_type === 'email') {
          return `            <div class="form-group">
                <label for="${fieldId}">${column.col_label}${isRequired ? ' *' : ''}</label>
                <input type="email" id="${fieldId}" name="${column.col_name}"${requiredAttr}>
            </div>`;
        } else if (column.col_type === 'phone' || column.col_type === 'tel') {
          return `            <div class="form-group">
                <label for="${fieldId}">${column.col_label}${isRequired ? ' *' : ''}</label>
                <input type="tel" id="${fieldId}" name="${column.col_name}"${requiredAttr}>
            </div>`;
        } else if (column.col_type === 'number' || column.col_type === 'numeric') {
          return `            <div class="form-group">
                <label for="${fieldId}">${column.col_label}${isRequired ? ' *' : ''}</label>
                <input type="number" id="${fieldId}" name="${column.col_name}"${requiredAttr}>
            </div>`;
        } else if (column.col_type === 'date') {
          return `            <div class="form-group">
                <label for="${fieldId}">${column.col_label}${isRequired ? ' *' : ''}</label>
                <input type="date" id="${fieldId}" name="${column.col_name}"${requiredAttr}>
            </div>`;
        } else if (column.col_type === 'dateTime') {
          return `            <div class="form-group">
                <label for="${fieldId}">${column.col_label}${isRequired ? ' *' : ''}</label>
                <input type="datetime-local" id="${fieldId}" name="${column.col_name}"${requiredAttr}>
            </div>`;
        } else {
          // Default to text input
          return `            <div class="form-group">
                <label for="${fieldId}">${column.col_label}${isRequired ? ' *' : ''}</label>
                <input type="text" id="${fieldId}" name="${column.col_name}"${requiredAttr}>
            </div>`;
        }
      })
      .join('\n            \n');
  };

  // Generate field data collection for JavaScript
  const generateFieldDataCollection = () => {
    return columns
      .map((column) => {
        const fieldId = `customer-${column.col_name}`;
        return `            ${column.col_name}: document.getElementById('${fieldId}').value,`;
      })
      .join('\n');
  };

  const integrationMethods = {
    jquery: {
      title: "jQuery AJAX",
      description: "Easy integration using jQuery library",
      icon: <Code className="w-5 h-5" />,
      code: `// Include jQuery in your HTML
// <script src="https://ajax.googleapis.com/ajax/libs/jquery/3.6.0/jquery.min.js"></script>

$("#contact-form").submit(function(e){
    e.preventDefault();

    const leadData = {
${generateFieldDataCollection()}
        access_code: "${accessCode}"
    };

    $.ajax({
        type: "POST",
        url: "${apiEndpoint}",
        data: JSON.stringify(leadData),
        contentType: "application/json",
        dataType: "json",
        success: function(response) {
            alert("Lead submitted successfully!");
            // Reset form or show success message
            $("#contact-form")[0].reset();
        },
        error: function(xhr, status, error) {
            alert("Error submitting lead. Please try again.");
            console.error("Error:", error);
        }
    });
});`
    },
    axios: {
      title: "Axios (Modern)",
      description: "Promise-based HTTP client for modern applications",
      icon: <Zap className="w-5 h-5" />,
      code: `// Install: npm install axios
// Or include via CDN: <script src="https://cdn.jsdelivr.net/npm/axios/dist/axios.min.js"></script>

async function submitLead() {
    try {
        const leadData = {
${generateFieldDataCollection()}
            access_code: '${accessCode}'
        };

        const response = await axios.post('${apiEndpoint}', leadData, {
            headers: {
                'Content-Type': 'application/json',
            }
        });

        if (response.data.success) {
            alert('Lead submitted successfully!');
            document.getElementById('contact-form').reset();
        }
    } catch (error) {
        alert('Error submitting lead. Please try again.');
        console.error('Error:', error);
    }
}

// Attach to form submit
document.getElementById('contact-form').addEventListener('submit', function(e) {
    e.preventDefault();
    submitLead();
});`
    },
    fetch: {
      title: "Vanilla JavaScript (Fetch)",
      description: "Native browser API, no dependencies required",
      icon: <Globe className="w-5 h-5" />,
      code: `// Pure JavaScript - no external libraries needed

async function submitLead() {
    try {
        const leadData = {
${generateFieldDataCollection()}
            access_code: '${accessCode}'
        };

        const response = await fetch('${apiEndpoint}', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(leadData)
        });

        const result = await response.json();

        if (result.success) {
            alert('Lead submitted successfully!');
            document.getElementById('contact-form').reset();
        } else {
            throw new Error(result.message || 'Submission failed');
        }
    } catch (error) {
        alert('Error submitting lead. Please try again.');
        console.error('Error:', error);
    }
}

// Event listener for form submission
document.addEventListener('DOMContentLoaded', function() {
    const form = document.getElementById('contact-form');
    if (form) {
        form.addEventListener('submit', function(e) {
            e.preventDefault();
            submitLead();
        });
    }
});`
    },
    html: {
      title: "Complete HTML Form",
      description: "Ready-to-use HTML form with styling",
      icon: <FileText className="w-5 h-5" />,
      code: `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Lead Capture Form</title>
    <style>
        .contact-form {
            max-width: 600px;
            margin: 0 auto;
            padding: 20px;
            font-family: Arial, sans-serif;
        }
        .form-group {
            margin-bottom: 15px;
        }
        .form-group label {
            display: block;
            margin-bottom: 5px;
            font-weight: bold;
            color: #333;
        }
        .form-group input,
        .form-group textarea,
        .form-group select {
            width: 100%;
            padding: 10px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 16px;
            box-sizing: border-box;
        }
        .form-group select {
            background-color: white;
        }
        .submit-btn {
            background-color: #007bff;
            color: white;
            padding: 12px 30px;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 16px;
            width: 100%;
        }
        .submit-btn:hover {
            background-color: #0056b3;
        }
        .success-message {
            display: none;
            padding: 15px;
            background-color: #d4edda;
            color: #155724;
            border: 1px solid #c3e6cb;
            border-radius: 4px;
            margin-bottom: 20px;
        }
        .error-message {
            display: none;
            padding: 15px;
            background-color: #f8d7da;
            color: #721c24;
            border: 1px solid #f5c6cb;
            border-radius: 4px;
            margin-bottom: 20px;
        }
    </style>
</head>
<body>
    <div class="contact-form">
        <h2>Contact Us</h2>
        <div id="success-message" class="success-message">
            Thank you! Your information has been submitted successfully.
        </div>
        <div id="error-message" class="error-message">
            Sorry, there was an error submitting your information. Please try again.
        </div>
        <form id="contact-form">
${generateFormFields()}

            <button type="submit" class="submit-btn">Submit</button>
        </form>
    </div>

    <script>
        async function submitLead() {
            try {
                const leadData = {
${generateFieldDataCollection()}
                    access_code: '${accessCode}'
                };

                const response = await fetch('${apiEndpoint}', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(leadData)
                });

                const result = await response.json();

                if (result.success) {
                    document.getElementById('success-message').style.display = 'block';
                    document.getElementById('error-message').style.display = 'none';
                    document.getElementById('contact-form').reset();

                    // Hide success message after 5 seconds
                    setTimeout(() => {
                        document.getElementById('success-message').style.display = 'none';
                    }, 5000);
                } else {
                    throw new Error(result.message || 'Submission failed');
                }
            } catch (error) {
                document.getElementById('error-message').style.display = 'block';
                document.getElementById('success-message').style.display = 'none';
                console.error('Error:', error);
            }
        }

        // Event listener for form submission
        document.addEventListener('DOMContentLoaded', function() {
            const form = document.getElementById('contact-form');
            if (form) {
                form.addEventListener('submit', function(e) {
                    e.preventDefault();
                    submitLead();
                });
            }
        });
    </script>
</body>
</html>`
    }
  };

  const features = [
    {
      icon: <Shield className="w-6 h-6 text-brandColor" />,
      title: "Secure Integration",
      description: "All form submissions are secured with access codes and validation"
    },
    {
      icon: <Zap className="w-6 h-6 text-green-600" />,
      title: "Real-time Processing",
      description: "Leads are instantly added to your CRM dashboard"
    },
    {
      icon: <Settings className="w-6 h-6 text-purple-600" />,
      title: "Customizable Fields",
      description: "Map form fields to your custom CRM columns"
    }
  ];

  if (loading) {
    return (
      <div className="w-full h-full flex flex-col">
        <div className="sticky top-0 z-10">
          <ModernSettingHeader
            title="Website Integration"
            description="Integrate lead capture forms into your website to automatically send leads to your CRM"
            icon={<Globe className="w-6 h-6" />} />
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex items-center justify-center h-64">
            <Loader size={42} text="Loading integration details..." />
          </div>
        </div>
      </div>
    );
  }

  if (!accessCode) {
    return (
      <div className="w-full h-full flex flex-col">
        <div className="sticky top-0 z-10">
          <ModernSettingHeader
            title="Website Integration"
            description="Integrate lead capture forms into your website to automatically send leads to your CRM"
            icon={<Globe className="w-6 h-6" />}
          />
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-yellow-900 dark:text-yellow-200 mb-3">
              No Website Integration Found
            </h3>
            <p className="text-yellow-800 dark:text-yellow-300">
              Please contact your administrator to set up website integration (integration_type 9) for your account.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      <div className="sticky top-0 z-10">
        <ModernSettingHeader
          title="Website Integration"
          description="Integrate lead capture forms into your website to automatically send leads to your CRM"
          icon={<Globe className="w-6 h-6" />}
        />
      </div>
      <div className="flex-1 overflow-y-auto p-6">
      {/* Features Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Integration Features
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div key={index} className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                {feature.icon}
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                  {feature.title}
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* API Configuration */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          API Configuration
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              API Endpoint
            </label>
            <div className="flex items-center space-x-2">
              <code className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-sm rounded-lg">
                {apiEndpoint}
              </code>
              <button
                onClick={() => copyToClipboard(apiEndpoint, 'endpoint')}
                className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                {copiedCode === 'endpoint' ? (
                  <CheckCircle className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Access Code
            </label>
            <div className="flex items-center space-x-2">
              <code className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-sm rounded-lg">
                {accessCode}
              </code>
              <button
                onClick={() => copyToClipboard(accessCode, 'access_code')}
                className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                {copiedCode === 'access_code' ? (
                  <CheckCircle className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Integration Methods */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          Integration Methods
        </h3>

        {/* Method Selector */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 dark:border-gray-700">
          {Object.entries(integrationMethods).map(([key, method]) => (
            <button
              key={key}
              onClick={() => setSelectedMethod(key as any)}
              className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                selectedMethod === key
                  ? 'bg-green-50 text-brandColor border-b-2 border-blue-500 dark:bg-blue-900/20 dark:text-blue-400'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {method.icon}
              <span>{method.title}</span>
            </button>
          ))}
        </div>

        {/* Selected Method Content */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-base font-medium text-gray-900 dark:text-white">
                {integrationMethods[selectedMethod].title}
              </h4>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {integrationMethods[selectedMethod].description}
              </p>
            </div>
            <button
              onClick={() => copyToClipboard(integrationMethods[selectedMethod].code, selectedMethod)}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              {copiedCode === selectedMethod ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Code
                </>
              )}
            </button>
          </div>

          {/* Code Block */}
          <div className="relative">
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm leading-relaxed">
              <code>{integrationMethods[selectedMethod].code}</code>
            </pre>
          </div>
        </div>
      </div>

      {/* Implementation Notes */}
      <div className="bg-green-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mt-6">
        <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-3">
          Implementation Notes
        </h3>
        <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-300">
          <li className="flex items-start space-x-2">
            <ArrowRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Replace the form field IDs with your actual HTML form field IDs</span>
          </li>
          <li className="flex items-start space-x-2">
            <ArrowRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Customize the success and error handling based on your requirements</span>
          </li>
          <li className="flex items-start space-x-2">
            <ArrowRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Test the integration thoroughly before deploying to production</span>
          </li>
          <li className="flex items-start space-x-2">
            <ArrowRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Monitor your CRM dashboard to ensure leads are being received correctly</span>
          </li>
        </ul>
      </div>
      </div>
    </div>
  );
};

export default WebsiteIntegrationPage;
